// Phase C1 Session 1.3 — Club Directory.
// Authenticated-only page listing every approved club member with quick
// call / text / email / view-profile actions. Alphabetically sorted by last
// name; search matches name, email and phone (formatting-agnostic).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MagnifyingGlass, Phone, ChatCircleText, EnvelopeSimple, User } from '@phosphor-icons/react';
import Avatar from '../components/Avatar';
import { directoryAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Normalize a phone value for search: keep digits only.
const digitsOnly = (s) => (s || '').replace(/\D/g, '');

// Highlight when the query is entirely digits (7+ chars) — treat as phone.
const isPhoneQuery = (q) => /^\d[\d\s().+-]{5,}\d$/.test(q.trim());

export default function DirectoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState(null);   // null = loading
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await directoryAPI.list();
        if (alive) setMembers(r.data?.members || []);
      } catch (e) {
        if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        setError('We couldn\u2019t load the directory. Please try again.');
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!members) return null;
    const q = query.trim().toLowerCase();
    if (!q) return members;
    const qDigits = digitsOnly(q);
    const phoneMode = isPhoneQuery(q) && qDigits.length >= 4;
    return members.filter((m) => {
      if (phoneMode) {
        const p = digitsOnly(m.phone);
        if (p && p.includes(qDigits)) return true;
      }
      const name = (m.full_name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      const fn = (m.first_name || '').toLowerCase();
      const ln = (m.last_name || '').toLowerCase();
      if (name.includes(q) || email.includes(q) || fn.includes(q) || ln.includes(q)) return true;
      // Also allow digit-substring matches on phone for mixed inputs.
      if (qDigits.length >= 3 && digitsOnly(m.phone).includes(qDigits)) return true;
      return false;
    });
  }, [members, query]);

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="directory-page">
      {/* Header + sticky search */}
      <div className="sticky top-0 z-10 bg-[#fbfaf8]/95 backdrop-blur-sm border-b border-[#e3dcd1] px-4 py-4">
        <div className="flex items-baseline justify-between mb-3">
          <h1 className="font-serif text-2xl sm:text-3xl text-[#013523] tracking-tight" data-testid="directory-title">
            Club Directory
          </h1>
          {members && (
            <span className="text-[11.5px] text-[#6a766e]" data-testid="directory-count">
              {members.length} member{members.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
          <input
            data-testid="directory-search"
            type="search"
            placeholder="Search members"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            className="w-full bg-white border border-[#e3dcd1] rounded-full pl-9 pr-4 py-2.5 text-[14px] text-[#013523] placeholder:text-[#94988f] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]"
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4">
        {error && (
          <div className="bg-[#fdf1f1] border border-[#f0c7c7] text-[#8a1c1c] rounded-xl p-3 text-[13px]" data-testid="directory-error">
            {error}
          </div>
        )}
        {members === null && !error && (
          <div className="space-y-2" data-testid="directory-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-[#f0ece3] border border-[#e3dcd1] rounded-2xl h-[76px]" />
            ))}
          </div>
        )}
        {members && filtered.length === 0 && (
          <div className="text-center py-16" data-testid="directory-empty">
            <User size={40} weight="thin" className="mx-auto text-[#94988f] mb-2" />
            <div className="text-[15px] text-[#013523] font-medium">No members found</div>
            <div className="text-[12px] text-[#6a766e] mt-1">Try a different name, email or number.</div>
          </div>
        )}
        {members && filtered.length > 0 && (
          <ul className="space-y-2" data-testid="directory-list">
            {filtered.map((m) => (
              <DirectoryCard key={m.id} member={m} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DirectoryCard({ member }) {
  const m = member;
  const phoneHref = m.phone ? `tel:${m.phone}` : null;
  const smsHref   = m.phone ? `sms:${m.phone}` : null;
  const emailHref = m.email ? `mailto:${m.email}` : null;
  const bothHidden = !m.phone && !m.email;
  return (
    <li
      data-testid={`directory-row-${m.id}`}
      className="bg-white border border-[#e3dcd1] rounded-2xl p-3 sm:p-4 flex items-start gap-3 hover:border-[#015e3f]/30 transition"
    >
      <Link
        to={`/player/${m.id}`}
        className="flex items-start gap-3 flex-1 min-w-0"
        data-testid={`directory-view-${m.id}`}
      >
        <Avatar src={m.avatar_url} name={m.full_name} size="md" />
        <div className="flex-1 min-w-0">
          {/* Line 1: Name + USTA pill on the same row */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[15.5px] sm:text-[16px] font-medium text-[#013523] truncate">
              {m.full_name}
            </span>
            {m.usta_rating && (
              <span className="inline-flex items-center rounded-full bg-[#fffbf0] border border-[#eddb9f] px-2 py-0.5 text-[11px] text-[#8a6a1c] font-semibold whitespace-nowrap">
                {m.usta_rating}
              </span>
            )}
          </div>
          {/* Line 2: Phone and Email — noticeably larger, easy to scan */}
          <div className="mt-1 space-y-0.5">
            {m.phone && (
              <div className="text-[13.5px] text-[#2a332c] tabular-nums truncate">
                {formatPhoneUS(m.phone)}
              </div>
            )}
            {m.email && (
              <div className="text-[13.5px] text-[#2a332c] truncate">{m.email}</div>
            )}
            {bothHidden && (
              <div className="text-[11.5px] text-[#94988f] italic" data-testid={`directory-hidden-${m.id}`}>
                Contact details hidden by member
              </div>
            )}
          </div>
        </div>
      </Link>
      {/* Actions — only appear when the corresponding field is visible */}
      <div className="flex items-center gap-1 shrink-0">
        {phoneHref && (
          <a href={phoneHref} data-testid={`directory-call-${m.id}`}
             className="w-9 h-9 rounded-full bg-[#e6f4ea] hover:bg-[#d5edda] text-[#015e3f] flex items-center justify-center"
             title={`Call ${m.full_name}`}>
            <Phone size={16} weight="bold" />
          </a>
        )}
        {smsHref && (
          <a href={smsHref} data-testid={`directory-text-${m.id}`}
             className="w-9 h-9 rounded-full bg-[#fffbf0] hover:bg-[#fff4d1] text-[#8a6a1c] flex items-center justify-center"
             title={`Text ${m.full_name}`}>
            <ChatCircleText size={16} weight="bold" />
          </a>
        )}
        {emailHref && (
          <a href={emailHref} data-testid={`directory-email-${m.id}`}
             className="w-9 h-9 rounded-full bg-[#f0ece3] hover:bg-[#e3dcd1] text-[#013523] flex items-center justify-center"
             title={`Email ${m.full_name}`}>
            <EnvelopeSimple size={16} weight="bold" />
          </a>
        )}
      </div>
    </li>
  );
}

// Pretty US phone: +17145551234 → (714) 555-1234
function formatPhoneUS(raw) {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}
